import React from "react";
import { Badge } from "./ui/badge";
import { MapPin, ArrowRight, Package, Clock } from "lucide-react";
import { money, fdate } from "../api";

export const STATUS_STYLES = {
  open: "bg-zinc-900 text-white border-zinc-900",
  assigned: "bg-zinc-100 text-zinc-800 border-zinc-300",
  picked_up: "bg-zinc-100 text-zinc-800 border-zinc-300",
  in_transit: "bg-zinc-800 text-white border-zinc-800",
  delivered: "bg-white text-zinc-900 border-zinc-900",
  disputed: "bg-white text-zinc-900 border-zinc-900",
  cancelled: "bg-zinc-100 text-zinc-400 border-zinc-200",
  pending: "bg-zinc-100 text-zinc-600 border-zinc-200",
  approved: "bg-zinc-900 text-white border-zinc-900",
  active: "bg-zinc-900 text-white border-zinc-900",
  paid: "bg-zinc-900 text-white border-zinc-900",
  connected: "bg-zinc-900 text-white border-zinc-900",
  free: "bg-zinc-100 text-zinc-800 border-zinc-300",
  trial: "bg-zinc-100 text-zinc-800 border-zinc-300",
  validated: "bg-zinc-100 text-zinc-800 border-zinc-300",
  rejected: "bg-white text-zinc-500 border-zinc-300",
  suspended: "bg-zinc-100 text-zinc-500 border-zinc-200",
  duplicate: "bg-zinc-100 text-zinc-400 border-zinc-200",
  invalid: "bg-white text-zinc-500 border-zinc-300",
  not_connected: "bg-zinc-100 text-zinc-500 border-zinc-200",
  pending_approval: "bg-white text-zinc-900 border-zinc-900",
  error: "bg-white text-zinc-900 border-zinc-900",
};

export function StatusPill({ status }) {
  return (
    <span data-testid={`status-${status}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${STATUS_STYLES[status] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

export const VEHICLE_LABELS = { bike: "Cargo bike", car: "Car", van_small: "Small van", van_large: "Large van", box_truck: "Box truck", semi_trailer: "Semi-trailer" };
export const SERVICE_LABELS = { standard: "Standard", same_day: "Same-day", hotshot: "Hotshot", dedicated: "Dedicated" };

export function JobCard({ job, selected, onClick, action }) {
  return (
    <button
      data-testid={`job-card-${job.ref}`}
      onClick={onClick}
      className={`w-full text-left bg-white border rounded-[28px] p-4 transition-all hover:border-[#1C1B1A]/25 hover:shadow-[0_10px_30px_rgba(28,27,26,0.06)] ${selected ? "border-[#1C1B1A] ring-2 ring-[#1C1B1A]/15" : "border-[#E8E4D9]"}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="font-mono text-xs text-zinc-500 font-medium">{job.ref}</span>
        <StatusPill status={job.status} />
      </div>
      <div className="flex items-center gap-1.5 text-sm font-semibold text-zinc-900 mb-1.5">
        <MapPin className="w-3.5 h-3.5 text-[#0A0A0A] shrink-0" />
        <span className="truncate">{job.pickup?.city || job.pickup?.address || "—"}</span>
        <ArrowRight className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
        <span className="truncate">{job.delivery?.city || job.delivery?.address || "—"}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
        <span>{VEHICLE_LABELS[job.vehicle_type] || job.vehicle_type}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{SERVICE_LABELS[job.service_level] || job.service_level}</span>
        {job.cargo?.weight_kg ? <span className="flex items-center gap-1"><Package className="w-3 h-3" />{job.cargo.weight_kg} kg</span> : null}
        {job.pickup_date && <span>{fdate(job.pickup_date)}</span>}
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-sm font-bold text-zinc-900">
          {job.pricing_mode === "bidding" ? (job.bid_count ? `${job.bid_count} bid${job.bid_count > 1 ? "s" : ""}` : "Open for bids") : money(job.price_gross ?? job.price_net, job.currency)}
        </span>
        {action}
      </div>
    </button>
  );
}

export function EmptyState({ title, body, testId = "empty-state" }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mb-3">
        <Package className="w-5 h-5 text-zinc-400" />
      </div>
      <p className="text-sm font-semibold text-zinc-900">{title}</p>
      {body && <p className="text-xs text-zinc-500 mt-1 max-w-xs">{body}</p>}
    </div>
  );
}

export function Stat({ label, value, sub }) {
  return (
    <div className="cr-card-glass !p-4">
      <p className="text-xs text-zinc-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-zinc-900 tracking-tight mt-1">{value}</p>
      {sub && <p className="text-[11px] text-zinc-400 mt-0.5">{sub}</p>}
    </div>
  );
}
